import { Injectable } from '@angular/core';
import { AuthService } from '../services/auth/auth.service';

@Injectable({ providedIn: 'root' })
export class AppService {
  constructor(private authService: AuthService) {}

  /**
   * Return the application id (claim `app_id`) from Keycloak tokenParsed.
   * Fall back to cookie named 'appid' when token does not contain the claim.
   * Returns null when no app id can be determined.
   */
  getAppId(): string {
    try {
      const tokenParsed = this.authService.getTokenParsed();
      const fromToken = (tokenParsed as any)?.app_id;
      if (fromToken && String(fromToken).trim() !== '') return String(fromToken);
    } catch (e) {
      // ignore and fallback to cookie
    }
    // fallback: return empty string when app id not found
    return '';
  }

  /**
   * Return the user's given name (first name) from tokenParsed or cookie fallback.
   */
  getFirstName(): string {
    try {
      const tokenParsed = this.authService.getTokenParsed() as any;
      const fromToken = tokenParsed?.given_name || tokenParsed?.prenom || tokenParsed?.prenoms || tokenParsed?.firstName;
      if (fromToken && String(fromToken).trim() !== '') return String(fromToken);
      // sometimes Keycloak stores full name in `name` — try to split
      const name = tokenParsed?.name;
      if (name && String(name).trim() !== '') {
        const parts = String(name).trim().split(' ');
        if (parts.length > 0) return parts.slice(1).join(' ') || parts[0];
      }
    } catch (e) {
      // ignore and fallback to cookie
    }
    return '';
  }

  /**
   * Return the user's family name (last name) from tokenParsed or cookie fallback.
   */
  getLastName(): string {
    try {
      const tokenParsed = this.authService.getTokenParsed() as any;
      const fromToken = tokenParsed?.family_name || tokenParsed?.nom || tokenParsed?.lastName;
      if (fromToken && String(fromToken).trim() !== '') return String(fromToken);
      const name = tokenParsed?.name;
      if (name && String(name).trim() !== '') {
        const parts = String(name).trim().split(' ');
        if (parts.length > 0) return parts[0];
      }
    } catch (e) {
      // ignore and fallback to cookie
    }
    return '';
  }

  /**
   * Return a combined display name.
   */
  getFullName(): string {
    const first = this.getFirstName();
    const last = this.getLastName();
    if (first && last) return `${first} ${last}`.trim();
    return first || last || '';
  }

  /**
   * Return a user profile label from tokenParsed or cookie fallback.
   */
  getProfile(): string {
    try {
      const tokenParsed = this.authService.getTokenParsed() as any;
      // custom claim names sometimes used
      const fromToken = tokenParsed?.profile || tokenParsed?.profil || tokenParsed?.role;
      if (fromToken && String(fromToken).trim() !== '') return String(fromToken);
    } catch (e) {
      // ignore
    }
    return '';
  }

  /**
   * Return the organisation claim from tokenParsed if present.
   * Claim name may be `Organisation` or `organisation` depending on Keycloak mapper.
   */
  getOrganisation(): string {
    try {
      const tokenParsed = this.authService.getTokenParsed() as any;
      const fromToken = tokenParsed?.Organisation || tokenParsed?.organisation || tokenParsed?.org || tokenParsed?.organisationId;
      if (fromToken && String(fromToken).trim() !== '') return String(fromToken);
    } catch (e) {
      // ignore
    }
    return '';
  }
}
